import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { CheckIcon } from 'lucide-react';
import { TIER_LIMITS, TIER_PRICING, type Tier } from '@mockd/shared';
import { PRICING_HEADLINE, SOCIAL_CARDS, pageTitle, socialImageMeta } from '@mockd/shared/brand';
import { ROUTES } from '../routes';
import { useAuth } from '../hooks';
import { Cursor } from '../components/common/Cursor';
import { Prompt, Terminal } from '../components/landing/Terminal';

interface PricingTier {
    tier: Tier;
    name: string;
    price: string;
    period: string;
    description: string;
    features: string[];
    cta: string;
    ctaLink: string;
    highlighted?: boolean;
    includesFrom?: string;
    disabled?: boolean;
    isCurrentPlan?: boolean;
    hideCta?: boolean;
}


const free = TIER_LIMITS.free;
const pro = TIER_LIMITS.pro;
const business = TIER_LIMITS.business;

const faqs = [
    {
        question: "What happens when I hit my plan limits?",
        answer: "You'll receive a notification as you approach your limits. Once reached, new requests to create projects or endpoints will be paused until you upgrade or free up resources. Existing endpoints continue to work normally.",
    },
    {
        question: "Can I upgrade or downgrade anytime?",
        answer: "Yes, though not self-serve yet. Email support@mockd.sh to move to Pro or Business, or back to Free. The change takes effect immediately, and moving to a lower plan never deletes your data.",
    },
    {
        question: "Is there a free trial for Pro?",
        answer: "We don't offer a time-limited trial, but our Free tier lets you explore all core features with generous limits. When you're ready for more, email us to move to Pro.",
    },
    {
        question: "What's included in the free tier?",
        answer: `The Free tier includes ${free.projects} projects with ${free.endpointsPerProject} endpoints each, custom subdomains, mock rules and conditions, response templates, request logging with ${free.logRetentionDays}-day retention, and request replay. It's built for personal projects — team collaboration starts on Pro.`,
    },
    {
        question: "Do I need a paid plan to use teams?",
        answer: `Yes. Free is personal-only, with no teams. Pro includes ${pro.maxTeams} team with up to ${pro.teamMembers} members. Business gives you unlimited teams with up to ${business.teamMembers} members each. A seat counts current members plus any outstanding pending invites.`,
    },
    {
        question: "How does billing work?",
        answer: "Online checkout isn't live yet, so there's no card to add and nothing is charged automatically. To get Pro or Business, email support@mockd.sh and we'll set it up.",
    },
    {
        question: "What are rule actions?",
        answer: "Rules can include action steps that run when requests match — make HTTP callouts to external services, store and retrieve variables across requests, and transform responses with expression templates. Actions are available as part of rules on the Pro plan.",
    },
];

const TIER_ORDER: Tier[] = ['free', 'pro', 'business'];

const META_DESCRIPTION = `Simple, transparent pricing for mockd. Start free with ${free.projects} projects and ${free.endpointsPerProject} endpoints each. Upgrade to Pro for team collaboration up to ${pro.teamMembers} members, or Business for unlimited teams.`;

const tiers: PricingTier[] = [
    {
        tier: 'free',
        name: 'Free',
        price: `$${TIER_PRICING.free.monthly}`,
        period: '/month',
        description: 'For personal projects and quick mocks',
        features: [
            'Personal workspace',
            `${free.projects} projects`,
            `${free.endpointsPerProject} endpoints per project`,
            `${free.rulesPerEndpoint} rules per endpoint`,
            `${free.logRetentionDays}-day log retention`,
            `${free.requestsPerDay.toLocaleString()} requests/day`,
            'Custom subdomains',
            'Mock rules & conditions',
            'Response templates',
            'Request replay from logs',
        ],
        cta: 'Sign up free',
        ctaLink: '/register',
    },
    {
        tier: 'pro',
        name: 'Pro',
        price: `$${TIER_PRICING.pro.monthly}`,
        period: '/month',
        description: 'For professionals who need more power',
        features: [
            `${pro.maxTeams} team, up to ${pro.teamMembers} members`,
            `${pro.projects} projects`,
            `${pro.endpointsPerProject} endpoints per project`,
            `${pro.rulesPerEndpoint} rules per endpoint`,
            `${pro.logRetentionDays}-day log retention`,
            `${pro.requestsPerDay.toLocaleString()} requests/day`,
            'Rule actions: HTTP callouts, variables, templates',
            `Global & project variables`,
            `Rate limits up to ${pro.maxEndpointRateLimit} req/min (vs ${free.maxEndpointRateLimit})`,
            `Larger payloads (${pro.maxResponseSize / 1024 / 1024}MB responses)`,
            'Priority support',
        ],
        cta: 'Get Pro',
        ctaLink: '/register',
        highlighted: true,
        includesFrom: 'Free',
    },
    {
        tier: 'business',
        name: 'Business',
        price: `$${TIER_PRICING.business.monthly}`,
        period: '/month',
        description: 'For teams that need room to grow',
        features: [
            // business.maxTeams is a sentinel ceiling (100), not a number worth
            // advertising — render it as "Unlimited" rather than interpolating it.
            `Unlimited teams, up to ${business.teamMembers} members each`,
            `${business.projects} projects`,
            `${business.endpointsPerProject} endpoints per project`,
            `${business.rulesPerEndpoint} rules per endpoint`,
            `${business.logRetentionDays}-day log retention`,
            `${business.requestsPerDay.toLocaleString()} requests/day`,
            `${business.globalVariables} global & ${business.projectVariables} project variables`,
            `Rate limits up to ${business.maxEndpointRateLimit} req/min`,
            `Larger payloads (${business.maxResponseSize / 1024 / 1024}MB responses)`,
            'Priority support',
        ],
        cta: 'Get Business',
        ctaLink: '/register',
        includesFrom: 'Pro',
    },
];

/** One column of the tier panel. The recommended tier is marked by a primary top rule, not a pill. */
function PricingColumn({ tier }: { tier: PricingTier }) {
    return (
        <div className="relative flex flex-col p-6 lg:p-8 min-w-0">
            {tier.highlighted && (
                <span aria-hidden="true" className="absolute inset-x-0 top-0 h-0.5 bg-primary" />
            )}
            <div className="lg:min-h-[11rem]">
                <h2 className="flex items-center gap-2">
                    {tier.name}
                    {tier.highlighted && (
                        <span className="font-mono text-xs font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded-selector">
                            recommended
                        </span>
                    )}
                </h2>
                <div className="mt-3">
                    <span className="font-heading text-4xl font-bold">{tier.price}</span>
                    {tier.period && (
                        <span className="text-base-content/70">{tier.period}</span>
                    )}
                </div>
                <p className="text-base-content/80 mt-2">{tier.description}</p>

                {tier.includesFrom && (
                    <p className="mt-6 text-sm text-base-content/70">
                        Everything in {tier.includesFrom}, plus:
                    </p>
                )}
            </div>

            <ul className="mt-6 pt-6 border-t border-base-300 space-y-2.5">
                {tier.features.map((feature, index) => (
                    <li key={index} className="flex gap-2.5 text-sm">
                        <span aria-hidden="true" className="font-mono text-primary">+</span>
                        <span className="text-base-content/80">{feature}</span>
                    </li>
                ))}
            </ul>

            <div className="mt-auto pt-8">
                {tier.hideCta ? null : tier.isCurrentPlan ? (
                    <div className="btn btn-ghost w-full pointer-events-none border border-base-content/20">
                        <CheckIcon className="size-4" />
                        Current plan
                    </div>
                ) : tier.disabled ? (
                    <div className="btn btn-ghost w-full pointer-events-none opacity-50">
                        {tier.cta}
                    </div>
                ) : (
                    <Link
                        to={tier.ctaLink}
                        className={`btn w-full ${
                            tier.highlighted ? 'btn-primary' : 'btn-outline'
                        }`}
                    >
                        {tier.cta}
                    </Link>
                )}
            </div>
        </div>
    );
}

export function Pricing() {
    const { user } = useAuth();
    const userTier = user?.tier;

    const displayTiers = tiers.map((tier) => {
        if (!user) return tier;

        const currentRank = TIER_ORDER.indexOf(userTier ?? 'free');
        const cardRank = TIER_ORDER.indexOf(tier.tier);

        if (cardRank === currentRank) {
            return { ...tier, isCurrentPlan: true };
        }
        // Plans below the user's current one aren't actionable from here.
        if (cardRank < currentRank) {
            return { ...tier, hideCta: true };
        }
        // Upgrades route into billing.
        return { ...tier, ctaLink: ROUTES.billing() };
    });

    return (
        <div className="min-h-screen bg-base-200">
            <Head>
                <title>{pageTitle('Pricing')}</title>
                <meta name="description" content={META_DESCRIPTION} />
                <meta property="og:title" content={pageTitle('Pricing')} />
                <meta property="og:description" content={META_DESCRIPTION} />
                <meta property="og:url" content="https://mockd.sh/pricing" />
                <meta name="twitter:title" content={pageTitle('Pricing')} />
                <meta name="twitter:description" content={META_DESCRIPTION} />
                {/* Rendered ahead of index.html's defaults, so crawlers take these first. */}
                {socialImageMeta(SOCIAL_CARDS.pricing).map(attrs => (
                    <meta key={'property' in attrs ? attrs.property : attrs.name} {...attrs} />
                ))}
            </Head>
            <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="max-w-3xl mb-10">
                    {/* The page's one headline `_` (docs/brand.md → The `_` cursor). */}
                    <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
                        {PRICING_HEADLINE}
                        <Cursor className="ml-2" />
                    </h1>
                    <p className="mt-4 text-lg text-base-content/80 max-w-2xl">
                        Every plan gets live mock URLs and real-time request logs. Paid plans
                        add teams, rule actions, longer log retention, and higher limits.
                    </p>
                </div>

                {/* Try without signing up */}
                {!user && (
                    <Terminal className="mb-10">
                        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
                            <span>
                                <Prompt />
                                no account needed to try it
                            </span>
                            <Link to="/?quickstart=true" className="link link-primary font-sans font-medium">
                                Start without signing up →
                            </Link>
                        </div>
                    </Terminal>
                )}

                {/* Tiers: one panel, a column per tier */}
                <div className="grid grid-cols-1 lg:grid-cols-3 rounded-box border border-base-300 bg-base-100 overflow-hidden divide-y lg:divide-y-0 lg:divide-x divide-base-300">
                    {displayTiers.map((tier) => (
                        <PricingColumn key={tier.name} tier={tier} />
                    ))}
                </div>

                {/* FAQ: every answer visible, no accordion */}
                <div className="mt-20">
                    <h2 className="text-3xl tracking-tight">Questions</h2>
                    <div className="mt-8 border-b border-base-300">
                        {faqs.map((faq) => (
                            <div
                                key={faq.question}
                                className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-8 gap-y-2 py-6 border-t border-base-300"
                            >
                                <h3>{faq.question}</h3>
                                <p className="text-base-content/80 leading-relaxed">
                                    {faq.answer}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer info */}
                <div className="mt-16">
                    <p className="text-base-content/70">
                        All plans include SSL, WebSocket support, and real-time request logging.
                    </p>
                    <p className="text-base-content/70 mt-2">
                        Questions?{' '}
                        <a
                            href="mailto:support@mockd.sh"
                            className="link link-primary"
                        >
                            Contact us
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}
